/**
 * Tooltip primitives for the custom (`ChartFrame`) path — verbatim
 * `@visx/tooltip`. For the XYChart scaffold, prefer its own `<Tooltip
 * renderTooltip={…} />` (exported from ./xychart), which wires positioning and
 * nearest-datum for you.
 *
 * `useTooltip` gives `{ tooltipOpen, tooltipLeft, tooltipTop, tooltipData,
 * showTooltip, hideTooltip }`; render into `TooltipWithBounds` (auto-flips at
 * viewport edges). `useTooltipInPortal` renders the tooltip in a portal to escape
 * `overflow: hidden`. Note `TooltipWithBounds` takes `nodeRef` (not `ref`).
 */
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { TooltipWithBounds } from "@visx/tooltip";

import { Box } from "../../components/ui/box";

export {
  useTooltip,
  useTooltipInPortal,
  TooltipWithBounds,
  defaultStyles as tooltipDefaultStyles,
} from "@visx/tooltip";

/**
 * svg-relative `{ x, y }` for a pointer event (verbatim `@visx/event`) — the
 * coordinate space {@link ChartTooltipCard} positions in. Call it from a mark's
 * (or a wrapping `ChartGroup`'s) `onMouseMove` on the custom `ChartFrame` path to
 * place a hover tooltip, exactly as `Pie`'s `onArcHover` does internally. Returns
 * `null` if the event has no owner `<svg>`.
 */
export { localPoint } from "@visx/event";

export type {
  UseTooltipParams,
  TooltipWithBoundsProps,
  UseTooltipPortalOptions,
} from "@visx/tooltip";

export type ChartHoverState<T> = {
  /** The hovered datum + its svg-relative card anchor, or `null` when idle. */
  hover: { datum: T; left: number; top: number } | null;
  /** Set the hover from a mark handler — `point` is the `localPoint` result. */
  show: (datum: T, point: { x: number; y: number } | null) => void;
  hide: () => void;
};

/**
 * Hover state for the custom (`ChartFrame`) path — the plumbing between a
 * mark's pointer handlers and {@link ChartTooltipCard}. Call `show(datum,
 * localPoint(event))` from `onMouseMove`/`onArcHover`, `hide()` on leave, and
 * render `hover && <ChartTooltipCard left={hover.left} top={hover.top}>` with
 * whatever content the chart owns. A `null` point is ignored (the pointer left
 * the svg), so callers pass the `localPoint` result straight through.
 */
export function useChartHover<T>(): ChartHoverState<T> {
  const [hover, setHover] = useState<{ datum: T; left: number; top: number } | null>(null);
  const show = useCallback((datum: T, point: { x: number; y: number } | null) => {
    if (point) setHover({ datum, left: point.x, top: point.y });
  }, []);
  const hide = useCallback(() => setHover(null), []);
  return { hover, show, hide };
}

export type ChartTooltipCardProps = {
  /** svg-relative x — e.g. the point from `Pie`'s `onArcHover`. */
  left: number;
  /** svg-relative y. */
  top: number;
  children: ReactNode;
};

/**
 * THE themed hover card for the custom (`ChartFrame`) path — compose `Text`/`Box`
 * content inside; the card chrome is an overlay-tier elevated chip, so it's
 * on-theme and reads above any substrate by construction. Render it as a SIBLING
 * of `ChartFrame` inside a `position="relative"` wrapper (it positions absolutely
 * against that wrapper, which must match the svg's bounds); `TooltipWithBounds`
 * flips it away from the container edges. Pointer-transparent, so it never steals
 * the hover. (The XYChart scaffold has its own `<ChartTooltip renderTooltip={…}/>`.)
 */
export function ChartTooltipCard({ left, top, children }: ChartTooltipCardProps) {
  return (
    <TooltipWithBounds
      left={left}
      top={top}
      offsetLeft={14}
      offsetTop={14}
      style={{ position: "absolute", pointerEvents: "none", zIndex: 10 }}
    >
      <Box surface="popover" rounded="lg" border paddingX="sm" paddingY="xs">
        {children}
      </Box>
    </TooltipWithBounds>
  );
}
